import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type TFunction } from "i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { type RouteComponentProps } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { Archive, ArchiveRestore, MapPinOff, Pencil, User, UserPlus } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import { type LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import leafletMarkerIconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import leafletMarkerIconUrl from "leaflet/dist/images/marker-icon.png";
import leafletMarkerShadowUrl from "leaflet/dist/images/marker-shadow.png";

import {
  aggregatorDetails as fetchAggregatorDetailsAction,
  aggregatorDetailsReset as resetAggregatorDetailsAction,
  archiveAggregator as archiveAggregatorAction,
  archiveAggregatorReset as resetArchiveAggregatorAction,
  unarchiveAggregator as unarchiveAggregatorAction,
  unarchiveAggregatorReset as resetUnarchiveAggregatorAction,
  listAggregatorAdministrators as fetchAggregatorAdministratorsAction,
  listAggregatorAdministratorsReset as resetAggregatorAdministratorsAction,
  listOrgTypes as fetchOrganizationTypesAction,
} from "../../actions";
import Loading from "../../components/Loading";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { WFPButton } from "../../components/ui/button";
import { displayErrorMessage } from "../../utils";
import { getLanguageLabelByCode, getLocalisedName } from "../../utils/i18n";
import {
  type Aggregator,
  type AggregatorAdmin,
  type GpsLocation,
} from "../Aggregators/components/AdminsTable/aggregator-admins-response";
import { AdministratorsTable } from "../AggregatorAdministrators/AdministratorsTable";
import { FarmersTable } from "../Farmers/FarmersTable";
import { useFarmersTable } from "../Farmers/useFarmersTable";
import { cn } from "@/lib/utils";
import { isExternalUser, isSystemAdministrator, isWfpManager, type UserData } from "@/utils/roles";

type AggregatorProfileRouteParams = {
  id: string;
};

type MetricCardIcon = typeof UserPlus;
type AggregatorProfileData = Partial<Aggregator>;

interface OrganizationType {
  active?: boolean;
  id: number | string;
  name: string;
}

interface AggregatorDetailsState {
  data?: AggregatorProfileData;
  errorMessage: string;
  isFetching: boolean;
}

interface OrganizationTypesState {
  data?: {
    results?: OrganizationType[];
  };
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  aggregatorDetails: AggregatorDetailsState;
  archiveAggregator: { errorMessage: string; isFetching: boolean };
  unarchiveAggregator: { errorMessage: string; isFetching: boolean };
  listAggregatorAdministrators: {
    data: { results?: AggregatorAdmin[] };
    isFetching: boolean;
  };
  listOrgTypes: OrganizationTypesState;
  userDetail: { data: UserData };
}

interface StateProps {
  aggregator?: AggregatorProfileData;
  archiveErrorMessage: string;
  unarchiveErrorMessage: string;
  errorMessage: string;
  isArchiving: boolean;
  isUnarchiving: boolean;
  isFetching: boolean;
  organizationTypes: OrganizationType[];
  currentUser: UserData;
}

interface DispatchProps {
  archiveAggregator: (id: string) => Promise<unknown>;
  unarchiveAggregator: (id: string) => Promise<unknown>;
  fetchAggregatorDetails: (id: string) => void;
  fetchOrganizationTypes: () => void;
  resetAggregatorDetails: () => void;
  resetArchiveAggregator: () => void;
  resetUnarchiveAggregator: () => void;
}

type AggregatorProfileProps = StateProps &
  DispatchProps &
  RouteComponentProps<AggregatorProfileRouteParams>;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

interface AggregatorMetric {
  label: string;
  value: number;
}

interface AggregatorMetricCard {
  icon: MetricCardIcon;
  id: string;
  metrics: AggregatorMetric[];
  title: string;
}

interface AggregatorDetailRow {
  label: string;
  value: string;
}

interface AggregatorProfileMapConfig {
  area: string;
  center: LatLngExpression;
  markerLabel: string;
  zoom: number;
}

const METRIC_CARD_ICON_BOX_CLASSNAME =
  "inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-neutral-alpha-100 text-muted-foreground";
const METRIC_CARD_ICON_CLASSNAME = "size-5";
const EMPTY_DETAIL_VALUE = "-";
const ALL_ITEMS_PAGE_SIZE = 9999;
const NO_ADMINISTRATORS: AggregatorAdmin[] = [];
const FARMERS_TAB_HIDDEN_COLUMNS = ["aggregator"];
const GOOGLE_LIKE_TILE_LAYER = {
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  subdomains: "abcd",
  url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
};

L.Icon.Default.mergeOptions({
  iconRetinaUrl: leafletMarkerIconRetinaUrl,
  iconUrl: leafletMarkerIconUrl,
  shadowUrl: leafletMarkerShadowUrl,
});

const DEFAULT_AGGREGATOR_MAP_ZOOM = 13;

const getLatestGpsLocation = (gpsLocations?: GpsLocation[]): GpsLocation | undefined => {
  if (!gpsLocations?.length) {
    return undefined;
  }

  return gpsLocations.reduce((latest, current) => {
    const latestTime = new Date(latest.backend_last_change_time ?? 0).getTime();
    const currentTime = new Date(current.backend_last_change_time ?? 0).getTime();

    return currentTime > latestTime ? current : latest;
  });
};

const createAggregatorMapConfig = (
  aggregator?: AggregatorProfileData
): AggregatorProfileMapConfig | null => {
  const latestGpsLocation = getLatestGpsLocation(aggregator?.gps_locations);

  if (!latestGpsLocation) {
    return null;
  }

  const latitude = Number(latestGpsLocation.latitude);
  const longitude = Number(latestGpsLocation.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const center: LatLngExpression = [latitude, longitude];

  return {
    area:
      latestGpsLocation.location_name?.trim() ||
      getLocalisedName(aggregator?.location_details) ||
      getAggregatorName(aggregator),
    center,
    markerLabel: `${latitude}, ${longitude}`,
    zoom: DEFAULT_AGGREGATOR_MAP_ZOOM,
  };
};

const AGGREGATOR_PROFILE_TABS = [
  { value: "details", labelKey: "aggregatorProfile.tabs.details" },
  { value: "administrators", labelKey: "aggregatorProfile.tabs.administrators" },
  { value: "farmers", labelKey: "aggregatorProfile.tabs.farmers" },
] as const;

type AggregatorProfileTab = (typeof AGGREGATOR_PROFILE_TABS)[number];
type AggregatorProfileTabValue = AggregatorProfileTab["value"];

const TAB_LIST_GRID_COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
};

const getAggregatorName = (aggregator?: AggregatorProfileData) => aggregator?.name?.trim() || "";

const formatDetailValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") {
    return EMPTY_DETAIL_VALUE;
  }

  return String(value);
};

const getAggregatorLocationName = (aggregator?: AggregatorProfileData) =>
  formatDetailValue(getLocalisedName(aggregator?.location_details));

const getAggregatorLanguageLabel = (aggregator?: AggregatorProfileData) =>
  formatDetailValue(getLanguageLabelByCode(aggregator?.language));

const getAggregatorOrganizationTypeName = (
  aggregator?: AggregatorProfileData,
  organizationTypes: OrganizationType[] = []
) => {
  const organizationType = organizationTypes.find(
    ({ id }) => String(id) === String(aggregator?.org_type)
  );

  return formatDetailValue(organizationType?.name);
};

const createAggregatorDetailRows = (
  t: TFunction,
  aggregator?: AggregatorProfileData,
  organizationTypes: OrganizationType[] = []
): AggregatorDetailRow[] => [
  {
    label: t("aggregatorProfile.details.administrativeAreaLocation"),
    value: getAggregatorLocationName(aggregator),
  },
  {
    label: t("aggregatorProfile.details.type"),
    value: getAggregatorOrganizationTypeName(aggregator, organizationTypes),
  },
  {
    label: t("aggregatorProfile.details.language"),
    value: getAggregatorLanguageLabel(aggregator),
  },
  {
    label: t("aggregatorProfile.details.physicalAddress"),
    value: formatDetailValue(aggregator?.physical_address),
  },
  {
    label: t("aggregatorProfile.details.targetNumberOfFarmers"),
    value: formatDetailValue(aggregator?.total_no_of_farmers),
  },
];

const AggregatorMetricCardsRow = ({
  cards,
  className,
}: {
  cards: AggregatorMetricCard[];
  className?: string;
}) => (
  <div
    className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4", className)}
  >
    {cards.map(card => (
      <AggregatorMetricCard key={card.id} card={card} />
    ))}
  </div>
);

const AggregatorMetricCard = ({ card }: { card: AggregatorMetricCard }) => {
  const Icon = card.icon;

  return (
    <Card className="gap-0 rounded-lg py-4 shadow-sm">
      <CardContent className="flex flex-col gap-3 px-4">
        <div className="flex min-w-0 items-center gap-2">
          <span className={METRIC_CARD_ICON_BOX_CLASSNAME}>
            <Icon className={METRIC_CARD_ICON_CLASSNAME} aria-hidden="true" />
          </span>
          <p className="truncate font-medium text-base text-foreground">{card.title}</p>
        </div>
        <ul className="flex flex-col gap-1">
          {card.metrics.map(metric => (
            <li
              key={`${card.id}-${metric.label}`}
              className="font-normal text-muted-foreground text-sm tabular-nums"
            >
              {metric.value} {metric.label}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const AggregatorProfileTabs = ({
  value,
  onValueChange,
  tabs,
}: {
  value: AggregatorProfileTabValue;
  onValueChange: (value: string) => void;
  tabs: readonly AggregatorProfileTab[];
}) => {
  const { t } = useTranslation();

  return (
    <Tabs value={value} onValueChange={onValueChange}>
      <TabsList className={cn("grid h-auto w-full sm:w-auto", TAB_LIST_GRID_COLS[tabs.length])}>
        {tabs.map(tab => (
          <TabsTrigger key={tab.value} value={tab.value} className="min-h-8">
            {t(tab.labelKey)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};

const AggregatorAdministratorsTab = ({ aggregatorId }: { aggregatorId: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const administrators = useSelector(
    (state: RootState) => state.listAggregatorAdministrators.data.results ?? NO_ADMINISTRATORS
  );
  const isFetching = useSelector(
    (state: RootState) => state.listAggregatorAdministrators.isFetching
  );

  useEffect(() => {
    // ponytail: fetch-all + filter in the browser. Swap for a server-side
    // location param if this payload ever gets big.
    dispatchAction(
      dispatch,
      fetchAggregatorAdministratorsAction({ page_size: ALL_ITEMS_PAGE_SIZE, active: true })
    );

    return () => {
      dispatchAction(dispatch, resetAggregatorAdministratorsAction());
    };
  }, [dispatch]);

  const aggregatorAdministrators = useMemo(
    () =>
      administrators.filter(administrator => String(administrator.aggregator?.id) === aggregatorId),
    [administrators, aggregatorId]
  );

  return (
    <AdministratorsTable
      administrators={aggregatorAdministrators}
      isFetchingAdministrators={isFetching}
    />
  );
};

const AggregatorFarmersTab = ({ aggregatorId }: { aggregatorId: string }) => {
  const { errorMessage, ...farmersTable } = useFarmersTable(aggregatorId);

  return (
    <>
      {displayErrorMessage(errorMessage)}
      <FarmersTable {...farmersTable} hiddenColumns={FARMERS_TAB_HIDDEN_COLUMNS} />
    </>
  );
};

interface AggregatorArchiveDialogProps {
  detailRows: AggregatorDetailRow[];
  errorMessage: string;
  isArchiving: boolean;
  name: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const AggregatorArchiveDialog = ({
  detailRows,
  errorMessage,
  isArchiving,
  name,
  onConfirm,
  onOpenChange,
  open,
}: AggregatorArchiveDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("aggregatorProfile.archiveDialog.title")}</DialogTitle>
          <DialogDescription>{t("aggregatorProfile.archiveDialog.description")}</DialogDescription>
        </DialogHeader>

        <dl className="grid gap-3 rounded-lg border border-neutral-alpha-300 bg-muted/20 p-4">
          <div className="grid gap-1">
            <dt className="font-medium text-muted-foreground text-xs">
              {t("aggregatorProfile.archiveDialog.name")}
            </dt>
            <dd className="text-sm">{name}</dd>
          </div>
          {detailRows.map(row => (
            <div key={row.label} className="grid gap-1">
              <dt className="font-medium text-muted-foreground text-xs">{row.label}</dt>
              <dd className="text-sm">{row.value}</dd>
            </div>
          ))}
        </dl>

        {errorMessage && displayErrorMessage(errorMessage)}

        <DialogFooter>
          <DialogClose asChild>
            <WFPButton type="button" variant="outline">
              {t("common.cancel")}
            </WFPButton>
          </DialogClose>
          <WFPButton type="button" variant="destructive" onClick={onConfirm} disabled={isArchiving}>
            <Archive className="size-4" aria-hidden="true" />
            {t("common.archive")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface AggregatorReactivateDialogProps {
  detailRows: AggregatorDetailRow[];
  errorMessage: string;
  isUnarchiving: boolean;
  name: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const AggregatorReactivateDialog = ({
  detailRows,
  errorMessage,
  isUnarchiving,
  name,
  onConfirm,
  onOpenChange,
  open,
}: AggregatorReactivateDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("aggregatorProfile.reactivateDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("aggregatorProfile.reactivateDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <dl className="grid gap-3 rounded-lg border border-neutral-alpha-300 bg-muted/20 p-4">
          <div className="grid gap-1">
            <dt className="font-medium text-muted-foreground text-xs">
              {t("aggregatorProfile.reactivateDialog.name")}
            </dt>
            <dd className="text-sm">{name}</dd>
          </div>
          {detailRows.map(row => (
            <div key={row.label} className="grid gap-1">
              <dt className="font-medium text-muted-foreground text-xs">{row.label}</dt>
              <dd className="text-sm">{row.value}</dd>
            </div>
          ))}
        </dl>

        {errorMessage && displayErrorMessage(errorMessage)}

        <DialogFooter>
          <DialogClose asChild>
            <WFPButton type="button" variant="outline">
              {t("common.cancel")}
            </WFPButton>
          </DialogClose>
          <WFPButton type="button" variant="success" onClick={onConfirm} disabled={isUnarchiving}>
            <ArchiveRestore className="size-4" aria-hidden="true" />
            {t("common.reactivate")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AggregatorDetailsCard = ({
  canManage,
  detailRows,
  isArchived,
  name,
  onArchive,
  onReactivate,
  onEdit,
}: {
  canManage: boolean;
  detailRows: AggregatorDetailRow[];
  isArchived: boolean;
  name: string;
  onArchive: () => void;
  onReactivate: () => void;
  onEdit: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Card className="w-full max-w-4xl rounded-lg py-0 shadow-sm">
      <CardContent className="flex items-start justify-between gap-4 p-6">
        <dl className="space-y-4">
          <div className="space-y-1">
            <dt className="font-medium text-muted-foreground text-xs">
              {t("aggregatorProfile.details.name")}
            </dt>
            <dd className="text-sm">{name}</dd>
          </div>
          {detailRows.map(row => (
            <div key={row.label} className="space-y-1">
              <dt className="font-medium text-muted-foreground text-xs">{row.label}</dt>
              <dd className="text-sm">{row.value}</dd>
            </div>
          ))}
        </dl>
        {canManage &&
          (isArchived ? (
            <div className="flex shrink-0 items-center gap-2">
              <WFPButton type="button" variant="success-secondary" onClick={onReactivate}>
                <ArchiveRestore className="size-4" aria-hidden="true" />
                {t("common.reactivate")}
              </WFPButton>
            </div>
          ) : (
            <div className="flex shrink-0 items-center gap-2">
              <WFPButton type="button" variant="outline" onClick={onEdit}>
                <Pencil className="size-4" aria-hidden="true" />
                {t("common.edit")}
              </WFPButton>
              <WFPButton type="button" variant="destructive-secondary" onClick={onArchive}>
                <Archive className="size-4" aria-hidden="true" />
                {t("common.archive")}
              </WFPButton>
            </div>
          ))}
      </CardContent>
    </Card>
  );
};

const AggregatorLocationPlaceholder = () => {
  const { t } = useTranslation();

  return (
    <div className="flex h-96 min-h-80 w-full flex-col items-center justify-center gap-4 rounded-lg border border-border border-dashed bg-muted/30 p-8 text-center">
      <span className="inline-flex size-14 items-center justify-center rounded-full bg-neutral-alpha-100 text-muted-foreground">
        <MapPinOff className="size-7" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        <p className="font-medium text-foreground text-sm">
          {t("aggregatorProfile.map.placeholderTitle")}
        </p>
        <p className="mx-auto max-w-xs text-muted-foreground text-sm">
          {t("aggregatorProfile.map.placeholder")}
        </p>
      </div>
    </div>
  );
};

const AggregatorLocationMap = ({ area, center, markerLabel, zoom }: AggregatorProfileMapConfig) => (
  <div
    aria-label={`Map of ${area}`}
    className="isolate relative z-0 h-96 min-h-80 w-full overflow-hidden rounded-lg border border-border bg-muted"
  >
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom={false}
      className="h-full w-full"
      attributionControl
    >
      <TileLayer
        attribution={GOOGLE_LIKE_TILE_LAYER.attribution}
        maxZoom={20}
        subdomains={GOOGLE_LIKE_TILE_LAYER.subdomains}
        url={GOOGLE_LIKE_TILE_LAYER.url}
      />
      <Marker position={center}>
        <Popup>
          <span className="font-medium">{area}</span>
          <br />
          {markerLabel}
        </Popup>
      </Marker>
    </MapContainer>
  </div>
);

const AggregatorProfile = ({
  aggregator,
  archiveAggregator,
  unarchiveAggregator,
  archiveErrorMessage,
  unarchiveErrorMessage,
  errorMessage,
  fetchAggregatorDetails,
  fetchOrganizationTypes,
  history,
  isArchiving,
  isUnarchiving,
  isFetching,
  match,
  organizationTypes,
  resetAggregatorDetails,
  resetArchiveAggregator,
  resetUnarchiveAggregator,
  currentUser,
}: AggregatorProfileProps) => {
  const { t } = useTranslation();
  const { id } = match.params;
  const [isArchiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AggregatorProfileTabValue>("details");
  const isArchived = aggregator?.active === false;
  const aggregatorName = getAggregatorName(aggregator);
  const profileName = aggregatorName;
  const detailRows = createAggregatorDetailRows(t, aggregator, organizationTypes);
  const mapConfig = createAggregatorMapConfig(aggregator);
  const canManageAggregator = isWfpManager(currentUser) || isSystemAdministrator(currentUser);
  // External users get a read-only slice: they see the tab bar and the Administrators
  // tab, but never the Farmers tab or the Edit/Archive controls.
  const canViewAdministrators = canManageAggregator || isExternalUser(currentUser);
  const canViewFarmers = canManageAggregator;
  const visibleTabs = AGGREGATOR_PROFILE_TABS.filter(
    tab => tab.value !== "farmers" || canViewFarmers
  );

  useEffect(() => {
    if (id) {
      fetchAggregatorDetails(id);
    }
    fetchOrganizationTypes();

    return () => {
      resetAggregatorDetails();
      resetArchiveAggregator();
      resetUnarchiveAggregator();
    };
  }, [
    fetchAggregatorDetails,
    fetchOrganizationTypes,
    id,
    resetAggregatorDetails,
    resetArchiveAggregator,
    resetUnarchiveAggregator,
  ]);

  const handleArchive = async () => {
    const result = await archiveAggregator(id);
    if (result) {
      setArchiveDialogOpen(false);
      history.push("/wfpManager/aggregators");
    }
  };

  const handleArchiveDialogOpenChange = (open: boolean) => {
    if (!open) {
      resetArchiveAggregator();
    }
    setArchiveDialogOpen(open);
  };

  const handleReactivate = async () => {
    const result = await unarchiveAggregator(id);
    if (result) {
      setReactivateDialogOpen(false);
      history.push("/wfpManager/aggregators");
    }
  };

  const handleReactivateDialogOpenChange = (open: boolean) => {
    if (!open) {
      resetUnarchiveAggregator();
    }
    setReactivateDialogOpen(open);
  };

  const renderTabContent = () => {
    if (canViewAdministrators && activeTab === "administrators") {
      return <AggregatorAdministratorsTab aggregatorId={id} />;
    }

    if (canViewFarmers && activeTab === "farmers") {
      return <AggregatorFarmersTab aggregatorId={id} />;
    }

    return (
      <div className="grid gap-8 lg:grid-cols-2">
        <AggregatorDetailsCard
          canManage={canManageAggregator}
          detailRows={detailRows}
          isArchived={isArchived}
          name={profileName}
          onArchive={() => setArchiveDialogOpen(true)}
          onReactivate={() => setReactivateDialogOpen(true)}
          onEdit={() => history.push(`/wfpManager/aggregatorDetails/${id}`)}
        />
        {mapConfig ? <AggregatorLocationMap {...mapConfig} /> : <AggregatorLocationPlaceholder />}
      </div>
    );
  };

  const renderContent = () => {
    if (isFetching) {
      return <Loading isVisible />;
    }

    if (errorMessage) {
      return <>{displayErrorMessage(errorMessage)}</>;
    }

    return (
      <div data-aggregator-id={id} data-aggregator-name={profileName} className="space-y-8">
        {/* <AggregatorMetricCardsRow cards={MOCK_AGGREGATOR_PROFILE.metricCards} /> */}
        {canViewAdministrators && (
          <AggregatorProfileTabs
            value={activeTab}
            onValueChange={tab => setActiveTab(tab as AggregatorProfileTabValue)}
            tabs={visibleTabs}
          />
        )}
        {renderTabContent()}
      </div>
    );
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("aggregators.breadcrumb"), href: "/wfpManager/aggregators" },
            { label: t("aggregatorDetails.aggregatorDetails") },
          ]}
        />
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-ivory-600 bg-ivory-100">
            <User />
          </span>
          <h1 className="font-semibold text-2xl">{profileName}</h1>
        </div>
      </div>
      {renderContent()}
      <AggregatorArchiveDialog
        detailRows={detailRows}
        errorMessage={archiveErrorMessage}
        isArchiving={isArchiving}
        name={profileName}
        onConfirm={handleArchive}
        onOpenChange={handleArchiveDialogOpenChange}
        open={isArchiveDialogOpen}
      />
      <AggregatorReactivateDialog
        detailRows={detailRows}
        errorMessage={unarchiveErrorMessage}
        isUnarchiving={isUnarchiving}
        name={profileName}
        onConfirm={handleReactivate}
        onOpenChange={handleReactivateDialogOpenChange}
        open={isReactivateDialogOpen}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  const organizationTypes = state.listOrgTypes.data?.results || [];

  return {
    aggregator: state.aggregatorDetails.data,
    archiveErrorMessage: state.archiveAggregator.errorMessage,
    unarchiveErrorMessage: state.unarchiveAggregator.errorMessage,
    errorMessage: state.aggregatorDetails.errorMessage || state.listOrgTypes.errorMessage,
    isArchiving: state.archiveAggregator.isFetching,
    isUnarchiving: state.unarchiveAggregator.isFetching,
    isFetching: state.aggregatorDetails.isFetching || state.listOrgTypes.isFetching,
    organizationTypes,
    currentUser: state.userDetail?.data ?? { user_groups: [] },
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  archiveAggregator: (id: string) =>
    dispatchAction(
      dispatch,
      archiveAggregatorAction({}, `${id}/archive/`)
    ) as unknown as Promise<unknown>,
  unarchiveAggregator: (id: string) =>
    dispatchAction(
      dispatch,
      unarchiveAggregatorAction({}, `${id}/unarchive/`)
    ) as unknown as Promise<unknown>,
  fetchAggregatorDetails: (id: string) =>
    dispatchAction(dispatch, fetchAggregatorDetailsAction(id)),
  fetchOrganizationTypes: () => dispatchAction(dispatch, fetchOrganizationTypesAction()),
  resetAggregatorDetails: () => dispatchAction(dispatch, resetAggregatorDetailsAction()),
  resetArchiveAggregator: () => dispatchAction(dispatch, resetArchiveAggregatorAction()),
  resetUnarchiveAggregator: () => dispatchAction(dispatch, resetUnarchiveAggregatorAction()),
});

export { AggregatorProfile };

export default connect(mapStateToProps, mapDispatchToProps)(AggregatorProfile);
